import { useState } from "react";
import { Link, useLocation } from "react-router";
import { business } from "../../config/business";
import { contactEmails } from "../../config/contact";
import { Brand } from "../ui/Brand";
import { ConsentBanner } from "../ui/ConsentBanner";
import { CharehyarAttribution } from "../ui/CharehyarAttribution";
import { Icon } from "../ui/Icon";
import { isPersianPath } from "../../i18n/routes";

const groups = [
  {
    title: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "For Manufacturers", href: "/for-manufacturers" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Available Products", href: "/products" },
      { label: "Sourcing Tips", href: "/#insights" },
      { label: "How It Works", href: "/how-it-works" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Product Sourcing", href: "/services" },
      { label: "Quote Comparison", href: "/services" },
      { label: "Supplier Verification", href: "/services" },
      { label: "Commercial Negotiation", href: "/services" },
      { label: "Trade Coordination", href: "/services" },
      { label: "Export Development", href: "/for-manufacturers" },
    ],
  },
] as const;

const persianGroups = [
  { key: "industries", title: "صنایع", links: [{ label: "فولاد و محصولات فولادی", href: "/fa/industries#steel" }, { label: "معدن و مواد معدنی", href: "/fa/industries#minerals" }, { label: "پمپ و تجهیزات صنعتی", href: "/fa/industries#pumps" }] },
  { key: "cooperation", title: "همکاری با ما", links: [{ label: "تأمین‌کنندگان", href: "/fa/suppliers" }, { label: "معرفی شرکت", href: "/fa/suppliers/join" }] },
  { key: "quick", title: "دسترسی سریع", links: [{ label: "درباره ما", href: "/fa/about" }, { label: "تماس با ما", href: "/fa/contact" }, { label: "English version", href: "/" }] },
] as const;

function FooterGroup({ idKey, title, children, open, onToggle }: { idKey?: string; title: string; children: React.ReactNode; open: boolean; onToggle: () => void }) {
  const panelId = `footer-${idKey ?? title.toLowerCase()}`;
  return (
    <section className={`footer-group ${open ? "is-open" : ""}`}>
      <h2 className="footer-desktop-title">{title}</h2>
      <button type="button" className="footer-accordion-button" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
        {title}<Icon name="chevron-down" size={17} />
      </button>
      <div className="footer-group-content" id={panelId}>{children}</div>
    </section>
  );
}

export function Footer() {
  const location = useLocation();
  const isFa = isPersianPath(location.pathname);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const toggle = (title: string) => setOpenGroup((current) => current === title ? null : title);

  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div className="footer-brand">
          <Brand compact homeHref={isFa ? "/fa/#top" : "/#top"} label={isFa ? "صفحه اصلی IranTradeReach" : "IranTradeReach home"} />
        </div>
        <div className="footer-groups">
          {(isFa ? persianGroups : groups).map((group) => (
            <FooterGroup idKey={"key" in group ? group.key : undefined} title={group.title} open={openGroup === group.title} onToggle={() => toggle(group.title)} key={group.title}>
              <ul>{group.links.map((link) => <li key={link.label}><Link to={link.href}>{link.label}</Link></li>)}</ul>
            </FooterGroup>
          ))}
          <FooterGroup idKey="contact" title={isFa ? "تماس" : "Contact"} open={openGroup === (isFa ? "تماس" : "Contact")} onToggle={() => toggle(isFa ? "تماس" : "Contact")}>
            <ul>
              <li><a href={`mailto:${contactEmails.general}`} dir="ltr">{contactEmails.general}</a></li>
              {isFa ? <><li><a href={`mailto:${contactEmails.partnership}`} dir="ltr">{contactEmails.partnership}</a></li><li><Link to="/fa/suppliers/join">معرفی شرکت</Link></li><li><Link to="/fa/contact">تماس با تیم</Link></li></> : <><li><a href={`mailto:${contactEmails.rfq}`}>{contactEmails.rfq}</a></li><li><a href={`mailto:${contactEmails.support}`}>{contactEmails.support}</a></li><li><a href={`mailto:${contactEmails.partnership}`}>{contactEmails.partnership}</a></li><li><Link to="/products?rfq=open#rfq">Start a sourcing request</Link></li><li><Link to="/contact">Contact our team</Link></li></>}
            </ul>
          </FooterGroup>
        </div>
      </div>
      <div className="container footer-bottom">
        <p>{isFa ? `© ۲۰۲۶ ${business.legalName}. تمامی حقوق محفوظ است.` : `© 2026 ${business.legalName}. All rights reserved.`} <span className="footer-attribution-divider" aria-hidden="true">·</span> <CharehyarAttribution variant="footer" locale={isFa ? "fa" : "en"} /></p>
        <div>{isFa ? <><Link to="/fa/privacy-policy">حریم خصوصی</Link><Link to="/fa/terms-of-use">شرایط استفاده</Link></> : <><Link to="/privacy-policy">Privacy</Link><Link to="/terms-of-use">Terms</Link><Link to="/cookie-policy">Cookies</Link></>}</div>
      </div>
      <ConsentBanner locale={isFa ? "fa" : "en"} />
    </footer>
  );
}
